import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Avatar,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

import {
  useAuthProfileRetrieveQuery,
  useAuthProfileUpdateMutation,
} from '../../../services/GLOWClient';
import { useAuth } from '../../../context/AuthContext';
import { theme } from '../../../theme';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import EmailLanguagePreference from './EmailLanguagePreference';

const ClientProfile = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isEditing, setIsEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const fileInputRef = useRef(null);

  const { data: userProfile, isLoading, error: profileError } = useAuthProfileRetrieveQuery(undefined, {
    skip: !isAuthenticated(),
  });

  const [updateProfile, { isLoading: isUpdating }] = useAuthProfileUpdateMutation();

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    email_language: 'fr',
    profile_photo: null,
  });

  // Source de vérité : userProfile > user
  const profile = userProfile || user;

  // Initialiser le formulaire
  useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        email_language: profile.email_language || 'fr',
        profile_photo: null,
      });
      setPreviewPhoto(profile.profile_photo || null);
    }
  }, [profile]);

  useEffect(() => {
    if (searchParams.get('section') !== 'communications') return;

    document.getElementById('communications')?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, [searchParams, profile]);

  // Nettoyer les blobs
  useEffect(() => {
    return () => {
      if (previewPhoto && previewPhoto.startsWith('blob:')) {
        URL.revokeObjectURL(previewPhoto);
      }
    };
  }, [previewPhoto]);

  const handleEditToggle = () => {
    if (isEditing) {
      if (profile) {
        setFormData({
          first_name: profile.first_name || '',
          last_name: profile.last_name || '',
          email: profile.email || '',
          phone: profile.phone || '',
          email_language: profile.email_language || 'fr',
          profile_photo: null,
        });
        setPreviewPhoto(profile.profile_photo || null);
      }
      setSuccessMessage('');
      setErrorMessage('');
    }
    setIsEditing(!isEditing);
  };

  const handleDeleteAccount = () => {
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirmation !== 'SUPPRIMER') {
      setErrorMessage('Vous devez taper "SUPPRIMER" en majuscules pour confirmer');
      return;
    }

    setDeleteLoading(true);
    setErrorMessage('');

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.delete('/api/auth/delete-account/', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        data: {
          email: user.email,
          password: 'authenticated_user' // Indicateur pour le backend
        }
      });

      if (response.status === 200) {
        // Implémenter la même logique que logout pour vider complètement le cache
        // 1. Clear local UI state IMMEDIATELY
        updateUser(null);

        // 2. Clear tokens and user data from storage
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        
        // 3. Reset RTK Query caches
        try {
          const { store } = require('../../../store');
          const { GLOWApiClient, dashboardApi, salonsApi } = require('../../../services/GLOWClient');
          store.dispatch(GLOWApiClient.util.resetApiState());
          store.dispatch(dashboardApi.util.resetApiState());
          store.dispatch(salonsApi.util.resetApiState());
        } catch (e) {
          console.warn('Failed to reset API caches on delete account', e);
        }

        // 4. Rediriger vers la landing page en bypassant la redirection automatique
        window.location.href = '/?bypass=true';
      }
    } catch (error) {
      setErrorMessage('Erreur lors de la suppression. Veuillez réessayer.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === 'profile_photo' && files && files[0]) {
      const file = files[0];
      setFormData((prev) => ({ ...prev, profile_photo: file }));

      if (previewPhoto && previewPhoto.startsWith('blob:')) {
        URL.revokeObjectURL(previewPhoto);
      }
      setPreviewPhoto(URL.createObjectURL(file));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = async () => {
    try {
      setSuccessMessage('');
      setErrorMessage('');

      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          data.append(key, value);
        }
      });

      const result = await updateProfile({ userRequest: data }).unwrap();
      updateUser(result);
      setPreviewPhoto(result.profile_photo || null);
      setSuccessMessage(t('client_profile.update_success'));
      setIsEditing(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setErrorMessage(
        error?.data?.message || t('client_profile.update_error')
      );
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (profileError || !profile) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {t('client_profile.load_error')}
      </Alert>
    );
  }

  return (
    <Box sx={{ py: 0 }}>
      {/* Header avec gradient */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[600]} 100%)`,
          color: 'white',
          py: { xs: 3, md: 6 },
          px: 3,
          mb: 4,
          borderRadius: '0 0 24px 24px',
          boxShadow: `0 8px 32px rgba(65, 101, 72, 0.15)`,
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, letterSpacing: '-0.02em', fontSize: { xs: '1.75rem', md: '3rem' } }}>
            {t('client_profile.profile')}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500, fontSize: { xs: '0.875rem', md: '1rem' } }}>
            {t('client_profile.manage_personal_info')}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3 }}>
        {/* Profile Header Card - Premium Design */}
        <Card
          sx={{
            borderRadius: 4,
            boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
            border: 'none',
            mb: 4,
            overflow: 'hidden',
            background: `linear-gradient(135deg, ${theme.colors.primary[50]} 0%, ${theme.colors.secondary[50]} 100%)`,
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Box sx={{ display: 'flex', gap: { xs: 2, md: 4 }, alignItems: 'flex-start', justifyContent: 'space-between' }}>
              {/* Avatar avec badge */}
              <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <Avatar
                  src={previewPhoto || undefined}
                  sx={{
                    width: { xs: 80, md: 120 },
                    height: { xs: 80, md: 120 },
                    background: `linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.secondary[500]} 100%)`,
                    fontSize: { xs: '2rem', md: '3rem' },
                    fontWeight: 800,
                    boxShadow: `0 12px 32px rgba(65, 101, 72, 0.25)`,
                    border: `4px solid white`,
                  }}
                >
                  {(!previewPhoto && !profile.profile_photo) &&
                    `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`}
                </Avatar>

                {isEditing && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      backgroundColor: theme.colors.primary[500],
                      borderRadius: '50%',
                      p: 1,
                      cursor: 'pointer',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }}
                    component="label"
                  >
                    <EditIcon sx={{ color: 'white', fontSize: 20 }} />
                    <input
                      ref={fileInputRef}
                      type="file"
                      name="profile_photo"
                      accept="image/*"
                      hidden
                      onChange={handleChange}
                    />
                  </Box>
                )}
              </Box>

              {/* Info principale */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, color: theme.colors.text.primary, mb: 0.5, letterSpacing: '-0.01em', fontSize: { xs: '1.25rem', md: '2.125rem' } }}>
                  {profile.first_name} {profile.last_name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: { xs: 1, md: 2 }, flexWrap: 'wrap' }}>
                  <Chip
                    label={t('client_profile.client')}
                    sx={{
                      backgroundColor: theme.colors.primary[100],
                      color: theme.colors.primary[700],
                      fontWeight: 600,
                      height: { xs: 24, md: 28 },
                      fontSize: { xs: '0.75rem', md: '0.875rem' },
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: theme.colors.text.secondary, fontWeight: 500, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                  {profile.email}
                </Typography>
              </Box>

              {/* Bouton Modifier - Gradient */}
              {!isEditing && (
                <Button
                  variant="contained"
                  onClick={handleEditToggle}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.colors.primary[600]} 0%, ${theme.colors.secondary[600]} 100%)`,
                    color: 'white',
                    fontWeight: 700,
                    py: { xs: 1, md: 1.2 },
                    px: { xs: 1, md: 2.5 },
                    borderRadius: { xs: '50%', md: '20px' },
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    boxShadow: `0 4px 16px rgba(65, 101, 72, 0.3)`,
                    border: 'none',
                    flexShrink: 0,
                    minWidth: { xs: 40, md: 'auto' },
                    width: { xs: 40, md: 'auto' },
                    height: { xs: 40, md: 'auto' },
                    display: 'flex',
                    gap: 1,
                    '&:hover': {
                      boxShadow: `0 8px 24px rgba(65, 101, 72, 0.4)`,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <EditIcon sx={{ display: { xs: 'block', md: 'block' } }} />
                  <Box component="span" sx={{ display: { xs: 'none', md: 'block' } }}>
                    {t('client_profile.edit_profile')}
                  </Box>
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        {!isEditing && (
          <Grid container spacing={3}>
            {/* Formulaire d'édition */}
            <Grid item xs={12} md={12}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              border: `1px solid ${theme.colors.primary[100]}`,
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: theme.colors.text.primary }}>
                  {t('client_profile.personal_info')}
                </Typography>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Messages */}
              {successMessage && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {successMessage}
                </Alert>
              )}
              {errorMessage && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errorMessage}
                </Alert>
              )}

              <Grid container spacing={3}>
                {/* Prénom */}
                <Grid item xs={12} sm={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, backgroundColor: theme.colors.primary[50], borderRadius: 2 }}>
                    <PersonIcon sx={{ color: theme.colors.primary[500], fontSize: 24 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: theme.colors.text.secondary, fontWeight: 600, display: 'block', mb: 0.5 }}>
                        {t('client_profile.first_name')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: theme.colors.text.primary }}>
                        {profile.first_name}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Nom */}
                <Grid item xs={12} sm={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, backgroundColor: theme.colors.primary[50], borderRadius: 2 }}>
                    <PersonIcon sx={{ color: theme.colors.primary[500], fontSize: 24 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: theme.colors.text.secondary, fontWeight: 600, display: 'block', mb: 0.5 }}>
                        {t('client_profile.last_name')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: theme.colors.text.primary }}>
                        {profile.last_name}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Email */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, backgroundColor: theme.colors.primary[50], borderRadius: 2 }}>
                    <EmailIcon sx={{ color: theme.colors.primary[500], fontSize: 24 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: theme.colors.text.secondary, fontWeight: 600, display: 'block', mb: 0.5 }}>
                        {t('client_profile.email')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: theme.colors.text.primary }}>
                        {profile.email}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Téléphone */}
                <Grid item xs={12} sm={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, backgroundColor: theme.colors.primary[50], borderRadius: 2 }}>
                    <PhoneIcon sx={{ color: theme.colors.primary[500], fontSize: 24 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: theme.colors.text.secondary, fontWeight: 600, display: 'block', mb: 0.5 }}>
                        {t('client_profile.phone')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: theme.colors.text.primary }}>
                        {profile.phone || '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
            </Grid>
          </Grid>
        )}

        {isEditing && (
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              border: `1px solid ${theme.colors.primary[100]}`,
              mt: 3,
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 4 } }}>
              {/* Boutons mobile uniquement - au-dessus du titre */}
              <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'space-between', mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleEditToggle}
                  sx={{
                    borderColor: theme.colors.error[300],
                    color: theme.colors.error[600],
                    minWidth: 40,
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    p: 1,
                    '&:hover': {
                      borderColor: theme.colors.error[500],
                      backgroundColor: theme.colors.error[50],
                    },
                  }}
                >
                  <CancelIcon />
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={isUpdating}
                  sx={{
                    backgroundColor: theme.colors.success[500],
                    minWidth: 40,
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    p: 1,
                    '&:hover': { backgroundColor: theme.colors.success[600] },
                    '&:disabled': { backgroundColor: theme.colors.success[300] },
                  }}
                >
                  {isUpdating ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                </Button>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: theme.colors.text.primary, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                  {t('client_profile.personal_info')}
                </Typography>

                {/* Boutons desktop */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleEditToggle}
                    sx={{
                      borderColor: theme.colors.error[300],
                      color: theme.colors.error[600],
                      '&:hover': {
                        borderColor: theme.colors.error[500],
                        backgroundColor: theme.colors.error[50],
                      },
                    }}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={isUpdating ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={handleSave}
                    disabled={isUpdating}
                    sx={{
                      backgroundColor: theme.colors.success[500],
                      '&:hover': { backgroundColor: theme.colors.success[600] },
                      '&:disabled': { backgroundColor: theme.colors.success[300] },
                    }}
                  >
                    {isUpdating ? t('client_profile.saving') : t('client_profile.save')}
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Messages */}
              {successMessage && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {successMessage}
                </Alert>
              )}
              {errorMessage && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errorMessage}
                </Alert>
              )}

              <Grid container spacing={3}>
                {/* First Name */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PersonIcon sx={{ color: theme.colors.primary[500], mr: 1, fontSize: 20 }} />
                    <Typography variant="caption" sx={{ color: theme.colors.text.secondary, fontWeight: 600 }}>
                      {t('client_profile.first_name')}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    size="small"
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>

                {/* Last Name */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PersonIcon sx={{ color: theme.colors.primary[500], mr: 1, fontSize: 20 }} />
                    <Typography variant="caption" sx={{ color: theme.colors.text.secondary, fontWeight: 600 }}>
                      {t('client_profile.last_name')}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    size="small"
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>

                {/* Email */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <EmailIcon sx={{ color: theme.colors.primary[500], mr: 1, fontSize: 20 }} />
                    <Typography variant="caption" sx={{ color: theme.colors.text.secondary, fontWeight: 600 }}>
                      {t('client_profile.email')}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    size="small"
                    required
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>

                {/* Phone */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PhoneIcon sx={{ color: theme.colors.primary[500], mr: 1, fontSize: 20 }} />
                    <Typography variant="caption" sx={{ color: theme.colors.text.secondary, fontWeight: 600 }}>
                      {t('client_profile.phone')}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>

              {isEditing && (
                <Alert severity="info" sx={{ mt: 3 }}>
                  {t('client_profile.save_notice')}
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        <Card
          id="communications"
          sx={{
            borderRadius: 3,
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            border: `1px solid ${theme.colors.primary[100]}`,
            mt: 3,
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: theme.colors.text.primary, mb: 1 }}>
              {t('client_profile.email_language')}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.colors.text.secondary, mb: 2 }}>
              {t('client_profile.email_language_help')}
            </Typography>
            <Box sx={{ maxWidth: 420 }}>
              <EmailLanguagePreference
                value={formData.email_language}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Section Mes rendez-vous */}
        {/* <Card
          sx={{
            borderRadius: 3,
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            border: `1px solid ${theme.colors.primary[100]}`,
            mt: 3,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
              Mes rendez-vous
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.colors.primary[50] }}>
                    <TableCell sx={{ fontWeight: 700, color: theme.colors.primary[700] }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: theme.colors.primary[700] }}>Heure</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: theme.colors.primary[700] }}>Salon</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: theme.colors.primary[700] }}>Service</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: theme.colors.primary[700] }}>Statut</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4, color: theme.colors.text.secondary }}>
                      <Typography variant="body2">Aucun rendez-vous pour le moment</Typography>
                      <Button variant="text" color="primary" sx={{ mt: 1 }}>
                        Prendre rendez-vous
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card> */}

        {/* Section Supprimer mon compte */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            border: '1px solid #fca5a5',
            mt: 3,
            bgcolor: '#fef2f2',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#dc2626' }}>
              Supprimer mon compte
            </Typography>
            <Divider sx={{ mb: 3, borderColor: '#fecaca' }} />
            
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
              La suppression de votre compte est définitive et irréversible. Toutes vos données personnelles seront anonymisées 
              conformément au RGPD et vous perdrez l'accès à votre historique de réservations.
            </Typography>
            
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteAccount}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Supprimer mon compte
            </Button>
          </CardContent>
        </Card>

        {/* Fenêtre de confirmation de suppression */}
        <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Confirmer la suppression du compte</DialogTitle>
          <DialogContent>
            <Typography variant="body1" paragraph>
              Êtes-vous absolument certain de vouloir supprimer votre compte ? Cette action est irréversible.
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph>
              Pour confirmer, tapez <strong>SUPPRIMER</strong> en majuscules dans le champ ci-dessous :
            </Typography>
            
            <TextField
              fullWidth
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder="Tapez SUPPRIMER"
              variant="outlined"
              margin="normal"
              error={errorMessage.includes('SUPPRIMER')}
            />
            
            {errorMessage && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errorMessage}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setOpenDeleteDialog(false);
              setDeleteConfirmation('');
              setErrorMessage('');
            }}>
              Annuler
            </Button>
            <Button 
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={deleteLoading || deleteConfirmation !== 'SUPPRIMER'}
              startIcon={deleteLoading ? <CircularProgress size={20} /> : null}
            >
              {deleteLoading ? 'Suppression...' : 'Confirmer la suppression'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default ClientProfile;
